import { useCallback, startTransition } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import { makeGetNotificationsForProject } from '../selectors/notifications';
import { makeGetProject } from '../selectors/projects';
import { makeGetProjectEmbeds } from '../selectors/embeds';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { enableProjectModule, disableProjectModule } from 'actions/projects';
import { fetchEmbeds, createEmbed } from 'actions/embeds';
import { showModal } from 'actions/modal';

import { trackEvent } from '../services/events-tracker-service';
import { filterNotifications } from '../utils/notification';
import { useFeatureAvailable } from 'shared-hooks/feature';
import { useCanUser } from 'shared-hooks/permission';

import CanUser from 'components/can-user';
import ProjectModulesConfig from 'components/ProjectModulesConfig';
import Tooltip from 'components/tooltip';

import IconArrowRight from 'icons/icon-arrow-right';
import IconMoreHoriz from 'icons/icon-more-horiz';
import IconPlus from 'icons/icon-plus';

const MODULES = ['home', 'milestones', 'task', 'calendar', 'discussion', 'documents', 'files', 'forms'];

const NavItem = ({ id, isEmbed, label, current, divided, hasNotifications, onClick }) => {
    const handleClick = () => onClick(id, isEmbed);

    return (
        <div className={'horizontal-nav-item' + (current === id ? ' active' : '') + (hasNotifications ? ' has-notifications' : '')} data-text={label} onClick={handleClick}>
            { label }

            { divided ? <IconArrowRight className="divider-icon" /> : null }
        </div>
    )
}

const ProjectNav = ({ embedsFetched, embedsLoading, projectId, notifications, sidebarExpanded, current, project, embeds }) => {
    const { t } = useTranslation(['common', 'milestone', 'task', 'chat', 'doc', 'file', 'project', 'form', 'date']);
    const history = useHistory();
    const hasFormsFeature = useFeatureAvailable('forms');
    const canSeeUpgradeFeatures = useCanUser('seeUpgradeFeatures');

    startTransition(() => {
        if (!embedsFetched && !embedsLoading) {
            fetchEmbeds({ projectId });
        }
    });

    const getNotifications = useCallback((filters) => {
        return filterNotifications(notifications, filters);
    }, [notifications]);

    const handleClick = (navItemId, isEmbed) => {
        trackEvent('HEADER_PROJECT_NAV_ITEM.CLICK', {
            navItem: isEmbed ? 'embed' : navItemId
        });

        if (navItemId === 'forms') {
            if (hasFormsFeature) {
                showModal('EDIT_AUTOMATION', {
                    initialPage: 'createForm',
                    projectId
                });
            } else {
                showModal('LOCKED_FEATURE', {
                    featureId: 'forms'
                }); 
            }
            return;
        }

        history.push(`/${projectId}${(isEmbed ? '/embed' : '')}/${navItemId}`);
    }

    const handleModuleToggle = (module, enabled) => {
        if (enabled) {
            enableProjectModule(projectId, module);
        } else {
            const disabledModules = (project?.disabled_modules || []);

            if (module === current || (module === 'milestones' && current === 'roadmap') || (module === 'tasks' && current === 'task')) {
                const nextModule = MODULES.find(m => !disabledModules.includes(m) && m !== current);

                history.push(`/${projectId}/${nextModule}`);
            }

            disableProjectModule(projectId, module);
        }
    }

    const handleCreateEmbedClick = () => {
        showModal('EMBED_BROWSE_MODAL', {
            projectId: projectId,
            onCreate: async ({ url, title }) => {
                const embed = await createEmbed({
                    title: title || url,
                    url,
                    project_id: projectId
                });

                history.push(`/${projectId}/embed/${embed.id}`);
            }
        });
    }

    const handleEditEmbedClick = embedId => {
        showModal('EMBED_EDIT_MODAL', {
            projectId: projectId,
            embedId
        });
    }

    const disabledModules = (project?.disabled_modules || []);

    return (
        <div className="horizontal-nav project-nav trimmed">
            <div className="horizontal-nav-main">
                {
                    !disabledModules.includes('home') ? (
                        <NavItem id="home" label={ t('Dashboard') } current={current} onClick={handleClick} divided={true} />
                    ) : null
                }

                {
                    !disabledModules.includes('milestones') ? (
                        <NavItem id="roadmap" label={ t('milestone:Roadmap') } current={current} onClick={handleClick} />
                    ) : null
                }

                {
                    !disabledModules.includes('tasks') ? (
                        <NavItem id="task" label={ t('task:Tasks') } current={current} onClick={handleClick} hasNotifications={Boolean(getNotifications({ type: 'task' }).length + getNotifications({ type: 'subtask' }).length)} />
                    ) : null
                }

                {
                    !disabledModules.includes('calendar') ? (
                        <NavItem id="calendar" label={ t('date:Calendar') } current={current} onClick={handleClick} hasNotifications={Boolean(getNotifications({ type: 'task' }).length + getNotifications({ type: 'subtask' }).length)} />
                    ) : null
                }

                {
                    !disabledModules.includes('discussion') ? (
                        <NavItem id="discussion" label={ t('chat:Discussions') } current={current} onClick={handleClick} hasNotifications={Boolean(getNotifications({ type: 'message' }).length + getNotifications({ type: 'thread' }).length)} />
                    ) : null
                }

                {
                    !disabledModules.includes('documents') ? (
                        <NavItem id="documents" label={ t('doc:Docs') } current={current} onClick={handleClick} hasNotifications={Boolean(getNotifications({ type: 'document' }).length)} />
                    ) : null
                }

                {
                    !disabledModules.includes('files') ? (
                        <NavItem id="files" label={ t('file:Files') } current={current} onClick={handleClick} hasNotifications={Boolean(getNotifications({ type: 'file' }).length)} />
                    ) : null
                }

                {
                    (!disabledModules.includes('forms') && (hasFormsFeature || canSeeUpgradeFeatures)) ? (
                        <NavItem id="forms" label={ t('form:Forms') } current={current} onClick={handleClick} />
                    ) : null
                }

                {
                    embeds.map(({ id, title }) => (
                        <NavItem key={id} id={id} isEmbed={true} label={ title } current={current} onClick={handleClick} />
                    ))
                }
            </div>

            <CanUser type="editProject" customList={[project?.owner]}>
                <div className="horizontal-nav-utils">
                    <div className="util">
                        <ProjectModulesConfig
                            asDropdown={true}
                            disabledModules={disabledModules}
                            embeds={embeds}
                            onChange={handleModuleToggle}
                            onEditEmbedClick={handleEditEmbedClick}
                            onCreateEmbedClick={handleCreateEmbedClick}
                        >
                            <div>
                                <Tooltip tooltip={ t('project:embed.tooltip') }>
                                    <button className="btn btn-xxsmall btn-icon">
                                        <IconMoreHoriz />
                                    </button>
                                </Tooltip>
                            </div>
                        </ProjectModulesConfig>
                    </div>
                </div>
            </CanUser>
        </div>
    );
}

const makeMapStateToProps = () => {
    const getNotificationsForProject = makeGetNotificationsForProject();
    const getProject = makeGetProject();
    const getProjectEmbeds = makeGetProjectEmbeds();
    const getEmbedsFetched = makeGetFetchedState();
    const getEmbedsLoading = makeGetLoadingState();

    return ({ sidebar, projects, notifications, embeds }, ownProps) => ({
        sidebarExpanded: sidebar.expanded,
        project: getProject(projects.items, ownProps.projectId),
        notifications: getNotificationsForProject(notifications.items, ownProps.projectId),
        embeds: getProjectEmbeds(embeds.items, ownProps.projectId),
        embedsFetched: getEmbedsFetched(embeds.fetchInfo, 'project', ownProps.projectId),
        embedsLoading: getEmbedsLoading(embeds.fetchInfo, 'project', ownProps.projectId)
    });
}

export default connect(makeMapStateToProps)(ProjectNav);
